import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheck, FiX, FiEye } from 'react-icons/fi';
import { where, orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Modal,
  Input,
  Pagination,
  CsvExportButton,
  BulkActionBar,
} from '../../components/UI';
import { useFirestoreCollection, usePagination } from '../../hooks';
import {
  getBusinessById,
  approveBusiness,
  rejectBusiness,
  suspendEntity,
  unsuspendEntity,
  reactivateEntity,
} from '../../services/adminService';
import EditEntityModal from '../../components/admin/EditEntityModal';
import { formatAddress, safeText } from '../../utils/format';
import styles from './Businesses.module.css';

// Schema (verified): businesses.{ ownerId, verificationStatus, isVerified, isActive, businessName, email, phone, address, ... }
// Status values: 'pending' | 'verified' | 'rejected'
const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const Businesses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'pending');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => {
    const c = [];
    // 'suspended' is a separate boolean flag (isSuspended) rather than a
    // verificationStatus value — filter on that instead.
    if (statusFilter === 'suspended') {
      c.push(where('isSuspended', '==', true));
    } else if (statusFilter !== 'all') {
      c.push(where('verificationStatus', '==', statusFilter));
    }
    c.push(orderBy('createdAt', 'desc'));
    c.push(limit(500));
    return c;
  }, [statusFilter]);
  const { data: businesses, loading } = useFirestoreCollection('businesses', constraints);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(businesses, 25);

  useEffect(() => {
    if (statusFilter !== 'pending') setSearchParams({ status: statusFilter });
    else setSearchParams({});
  }, [statusFilter, setSearchParams]);

  const openBusinessDetail = async (business) => {
    setErrorMsg('');
    try {
      const fullBusiness = await getBusinessById(business.id);
      setSelectedBusiness(fullBusiness);
      setShowDetailModal(true);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Failed to load business: ${err.message}`);
    }
  };

  const handleApprove = async (business) => {
    try {
      setActionLoading(true);
      await approveBusiness(business.id);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Approve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedBusiness || !rejectReason) return;
    try {
      setActionLoading(true);
      await rejectBusiness(selectedBusiness.id, rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedBusiness(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Reject failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedBusiness) return;
    if (!window.confirm(`Suspend ${selectedBusiness.businessName}? They won't appear to users until unsuspended.`)) return;
    try {
      setActionLoading(true);
      await suspendEntity({
        entityType: 'business',
        entityId: selectedBusiness.id,
        reason: 'Suspended by admin',
      });
      setShowDetailModal(false);
      setSelectedBusiness(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Suspend failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!selectedBusiness) return;
    if (!window.confirm(`Reinstate ${selectedBusiness.businessName}?`)) return;
    try {
      setActionLoading(true);
      await unsuspendEntity({
        entityType: 'business',
        entityId: selectedBusiness.id,
      });
      setShowDetailModal(false);
      setSelectedBusiness(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Unsuspend failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedBusiness) return;
    if (!window.confirm(`Reactivate this rejected business back to pending? They can resubmit / be reapproved.`)) return;
    try {
      setActionLoading(true);
      await reactivateEntity({ entityType: 'business', entityId: selectedBusiness.id });
      setShowDetailModal(false);
      setSelectedBusiness(null);
    } catch (err) {
      setErrorMsg(`Reactivate failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  };
  const toggleAllOnPage = (checked) => {
    const ids = pageData.map((b) => b.id);
    setSelectedIds((prev) => {
      if (checked) return Array.from(new Set([...prev, ...ids]));
      const drop = new Set(ids);
      return prev.filter((x) => !drop.has(x));
    });
  };
  const clearSelection = () => setSelectedIds([]);

  const bulkApprove = async () => {
    if (!window.confirm(`Approve ${selectedIds.length} businesses?`)) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => approveBusiness(id)));
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk approve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const bulkReject = async () => {
    const reason = window.prompt('Reason for rejecting selected businesses:');
    if (!reason) return;
    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => rejectBusiness(id, reason)));
      clearSelection();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Bulk reject failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = { pending: 'warning', verified: 'success', rejected: 'danger' };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'unknown'}</Badge>;
  };

  const csvColumns = [
    { key: 'id', label: 'Business ID' },
    { key: 'businessName', label: 'Name' },
    { key: 'businessType', label: 'Type' },
    { key: 'verificationStatus', label: 'Status' },
    { key: 'ownerId', label: 'Owner UID' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'createdAt', label: 'Applied', accessor: (b) => formatDate(b.createdAt) },
    { key: 'verifiedAt', label: 'Verified At', accessor: (b) => formatDate(b.verifiedAt) },
    { key: 'rejectionReason', label: 'Rejection Reason' },
  ];

  const columns = [
    {
      key: 'businessName',
      title: 'Business',
      render: (value, row) => (
        <div className={styles.businessCell}>
          <div className={styles.logo}>
            {row.logo || row.coverImage ? (
              <img src={row.logo || row.coverImage} alt="" />
            ) : (
              <span>{(value || row.name || 'B').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className={styles.businessInfo}>
            <span className={styles.businessName}>{safeText(value || row.name || 'Unnamed Business')}</span>
            <span className={styles.businessType}>{safeText(row.businessType || row.type || '—')}</span>
          </div>
        </div>
      ),
    },
    { key: 'email', title: 'Email', render: (value, row) => safeText(value || row.contactEmail || '-') },
    {
      key: 'verificationStatus',
      title: 'Status',
      render: (value) => getStatusBadge(value),
    },
    { key: 'createdAt', title: 'Applied', render: (value) => formatDate(value) },
    {
      key: 'actions',
      title: '',
      width: '210px',
      render: (_, row) => (
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" icon={<FiEye />} onClick={() => openBusinessDetail(row)} />
          {row.verificationStatus === 'pending' && (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<FiCheck />}
                onClick={() => handleApprove(row)}
                disabled={actionLoading}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={<FiX />}
                onClick={() => {
                  setSelectedBusiness(row);
                  setShowRejectModal(true);
                }}
                disabled={actionLoading}
              >
                Reject
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Business Applications</h1>
          {!loading && <Badge variant="primary">{businesses.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Review and manage business verification requests
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton
            rows={businesses}
            columns={csvColumns}
            filename={`dierend-businesses-${statusFilter}`}
          />
        </div>

        <BulkActionBar selectedCount={selectedIds.length} onClear={clearSelection}>
          <Button variant="primary" size="sm" onClick={bulkApprove} disabled={actionLoading}>
            Approve Selected
          </Button>
          <Button variant="danger" size="sm" onClick={bulkReject} disabled={actionLoading}>
            Reject Selected
          </Button>
        </BulkActionBar>

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage="No business applications found"
          selectable
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAllOnPage}
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Business Details"
        size="lg"
        footer={
          selectedBusiness?.verificationStatus === 'pending' ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowDetailModal(false);
                  setShowRejectModal(true);
                }}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handleApprove(selectedBusiness);
                  setShowDetailModal(false);
                }}
                loading={actionLoading}
              >
                Approve
              </Button>
            </>
          ) : selectedBusiness?.isSuspended ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button variant="ghost" onClick={() => setShowEditModal(true)}>
                Edit profile
              </Button>
              <Button variant="primary" loading={actionLoading} onClick={handleUnsuspend}>
                Unsuspend
              </Button>
            </>
          ) : selectedBusiness?.verificationStatus === 'verified' ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button variant="ghost" onClick={() => setShowEditModal(true)}>
                Edit profile
              </Button>
              <Button variant="danger" loading={actionLoading} onClick={handleSuspend}>
                Suspend
              </Button>
            </>
          ) : selectedBusiness?.verificationStatus === 'rejected' ? (
            <>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>Close</Button>
              <Button variant="primary" loading={actionLoading} onClick={handleReactivate}>
                Reactivate (back to pending)
              </Button>
            </>
          ) : (
            // any other unrecognized status — Close-only fallback so the
            // modal isn't footerless and the only exit is the X icon.
            <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          )
        }
      >
        {selectedBusiness && (
          <div className={styles.detailContent}>
            <div className={styles.detailHeader}>
              <div className={styles.logoLarge}>
                {selectedBusiness.logo ? (
                  <img src={selectedBusiness.logo} alt="" />
                ) : (
                  <span>{(selectedBusiness.businessName || 'B').charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div>
                <h3>{safeText(selectedBusiness.businessName || selectedBusiness.name)}</h3>
                <p>{safeText(selectedBusiness.businessType || selectedBusiness.type || '—')}</p>
                {getStatusBadge(selectedBusiness.verificationStatus)}
              </div>
            </div>

            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Owner UID</span>
                <span className={styles.detailValue}>{safeText(selectedBusiness.ownerId)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Email</span>
                <span className={styles.detailValue}>{safeText(selectedBusiness.email)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Phone</span>
                <span className={styles.detailValue}>{safeText(selectedBusiness.phone)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Website</span>
                <span className={styles.detailValue}>{safeText(selectedBusiness.website)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Address</span>
                <span className={styles.detailValue}>
                  {formatAddress(selectedBusiness.address || selectedBusiness.location)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Applied</span>
                <span className={styles.detailValue}>{formatDate(selectedBusiness.createdAt)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Verified</span>
                <span className={styles.detailValue}>{formatDate(selectedBusiness.verifiedAt)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tax ID</span>
                <span className={styles.detailValue}>{safeText(selectedBusiness.taxId)}</span>
              </div>
            </div>

            {selectedBusiness.description && (
              <div className={styles.description}>
                <h4>Description</h4>
                <p>{safeText(selectedBusiness.description)}</p>
              </div>
            )}

            {Array.isArray(selectedBusiness.documents) && selectedBusiness.documents.length > 0 && (
              <div className={styles.description}>
                <h4>Documents</h4>
                <ul>
                  {selectedBusiness.documents.map((d, i) => (
                    <li key={i}>
                      <a href={d.url || d} target="_blank" rel="noreferrer">
                        {d.name || d.url || `Document ${i + 1}`}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedBusiness.rejectionReason && (
              <div className={styles.rejectInfo}>
                <h4>Rejection Reason</h4>
                <p>{selectedBusiness.rejectionReason}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        title="Reject Business Application"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} loading={actionLoading} disabled={!rejectReason}>
              Reject
            </Button>
          </>
        }
      >
        <p className={styles.rejectText}>
          You are about to reject{' '}
          <strong>{selectedBusiness?.businessName || selectedBusiness?.name}</strong>. Please provide a reason:
        </p>
        <Input
          placeholder="Enter rejection reason..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      <EditEntityModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        entityType="business"
        entity={selectedBusiness}
        onSaved={() => {
          // Re-pull the fresh business doc so the detail modal reflects the edit.
          if (selectedBusiness?.id) {
            getBusinessById(selectedBusiness.id).then((b) => b && setSelectedBusiness(b));
          }
        }}
      />
    </div>
  );
};

export default Businesses;
